import React, { useEffect, useState } from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";
import MenuIcon from "@mui/icons-material/Menu";
import Container from "@mui/material/Container";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import MenuItem from "@mui/material/MenuItem";
import {
  bottomDirectionArrow,
  notificationDot,
  notificationIcon,
  messagingIcon,
  jamboCareLogo,
} from "../../../assets";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setIsLoggedIn } from "../../../screens/login-screen/LoginSlice";
import EventEHRSmallScreenMenuDrawer from "./EventEHRSmallScreenMenuDrawer";
import useDeviceType from "../../../custom-hooks/DeviceType";
import {
  getLoginUserData,
  getLoginUserId,
  shouldShowSwitchToProvider,
} from "../../../utils/getToken";
import useSocketNotifications from "../../../custom-hooks/UseSocketNotifications";
import EventEHRConfirmationDialog from "../../Molecules/confimation-dialog/EventEHRConfirmationDialog";
import FacilityDropdown from "../../Molecules/FacilityDropdown/FacilityDropdown";

const pages = [
  "Dashboard",
  "Facility-management",
  "Inventory-management",
  "Orders",
  "Documents",
  "Contact-directory",
  "Tasks",
  "Reports",
  "Settings",
];
const settings = ["Logout"];

interface HandlePageChangeProps {
  handlePageChange: (event: any) => void;
}

function EventEMRResponsiveAppBar({ handlePageChange }: HandlePageChangeProps) {
  const loginUserData: any = getLoginUserData();
  const parsedLoginUserData = JSON.parse(loginUserData);
  const loggedInUserName = parsedLoginUserData?.name?.toUpperCase();
  const dispatch = useDispatch();
  const parseLoginUserId: any = getLoginUserId();
  const loginUserId = JSON.parse(parseLoginUserId);
  const {
    notifications,
    hasNew,
    setHasNew,
    clearNotifications,
    broadcastNotification,
    dismissBroadcast,
  } = useSocketNotifications(loginUserId);
  const path = window.location.pathname.split("/")[1];
  const navigate = useNavigate();
  const deviceType = useDeviceType();
  const showMenu =
    deviceType === "desktop" ||
    deviceType === "extra-large-desktop" ||
    deviceType === "large-desktop";
  const [openMenu, setOpenMenu] = useState(false);
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
    null,
  );
  const [selectedPage, setSelectedPage] = React.useState<string>(
    path ? path?.charAt(0)?.toUpperCase() + path?.slice(1) : "Dashboard",
  ); // Track selected page
  const [showSwitchToProvider, setShowSwitchToProvider] = React.useState(false);

  // Search field data
  const searchFieldData = [
    { label: "Jayesh Sevatkar", value: "111" },
    { label: "Shubham Patil", value: "222" },
  ];

  useEffect(() => {
    setSelectedPage(
      path ? path?.charAt(0)?.toUpperCase() + path?.slice(1) : "Dashboard",
    );
  }, [path]);

  // Check if switch to provider button should be shown
  useEffect(() => {
    const shouldShow = shouldShowSwitchToProvider();
    setShowSwitchToProvider(shouldShow);
  }, []);

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setOpenMenu(true);
  };
  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = (value: any) => {
    switch (value) {
      case "Logout":
        localStorage.clear();
        sessionStorage.clear();
        dispatch(setIsLoggedIn(false));
        window.location.href = "/login"; // Redirect to login page

        break;
      case "Profile":
        navigate("/profile");
        break;
    }
    setAnchorElUser(null);
  };

  const handlePageClick = (page: string) => {
    handlePageChange(page);
    setSelectedPage(page); // Set the selected page
  };

  const handleSearchChangeValue = (e: any) => {
    console.log("searchValue:", e?.target?.value);
  };

  const handleSelectedSearchValue = (selectedValue: any) => {
    if (selectedValue) {
      navigate(`/patient/${selectedValue?.value}`);
      setOpenMenu(false);
    }
  };

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        backgroundColor: "#FFFFFF",
        borderBottom: "1px solid #F4F4F4",
      }}
    >
      <Container maxWidth={false}>
        <Toolbar disableGutters>
          {/* Logo for large screens */}
          <Typography
            variant="h6"
            noWrap
            component="a"
            // href="#app-bar-with-responsive-menu"
            onClick={() => {
              navigate("/dashboard");
              setSelectedPage("Dashboard");
            }}
            sx={{
              mr: 2,
              // display: { xs: "none", md: "flex" }, // Visible only on medium and larger screens
              display: showMenu ? "flex" : "none",
              fontFamily: "monospace",
              fontWeight: 700,
              letterSpacing: ".3rem",
              color: "inherit",
              textDecoration: "none",
            }}
          >
            <img
              src={jamboCareLogo}
              alt="logo"
              style={{
                maxWidth: "140px",
                maxHeight: "120px",
                objectFit: "contain",
              }}
            />
          </Typography>

          {/* Mobile menu button */}
          <Box
            sx={{
              flexGrow: 1,
              // display: { xs: "flex", md: "none" },
              display: showMenu ? "none" : "flex",
            }}
          >
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              color="inherit"
              style={{ color: "black" }}
            >
              <MenuIcon />
            </IconButton>
          </Box>

          {/* Logo for mobile */}
          <Typography
            variant="h5"
            noWrap
            component="a"
            sx={{
              mr: 2,
              // display: { xs: "flex", md: "none" },
              display: showMenu ? "none" : "flex",
              flexGrow: 1,
              fontFamily: "monospace",
              fontWeight: 700,
              letterSpacing: ".3rem",
              color: "inherit",
              textDecoration: "none",
            }}
            onClick={() => {
              navigate("/dashboard");
              setSelectedPage("Dashboard");
            }}
          >
            <img
              src={jamboCareLogo}
              alt="logo"
              style={{
                maxWidth: "140px",
                maxHeight: "120px",
                objectFit: "contain",
              }}
            />{" "}
          </Typography>

          {/* Desktop navigation menu */}
          <Box
            sx={{
              flexGrow: 1,

              // display: {
              //   // xs: "none",
              //   // md: "flex",
              //   marginLeft: "22px",
              // },
              display: showMenu ? "flex" : "none",
            }}
          >
            {pages.map((page) => (
              <Button
                key={page}
                onClick={() => {
                  handlePageClick(page);
                }} // Update selected page on click
                sx={{
                  my: 2,
                  gap: "10px",
                  marginRight: "14px",
                  fontSize: "16px",
                  fontWeight: page === selectedPage ? 600 : 500,
                  textTransform: "none",
                  color: page === selectedPage ? "#005596" : "#979797", // Set color dynamically
                  display: "block",
                  whiteSpace: "noWrap",
                }}
              >
                {page === "Facility-management"
                  ? "Facility Management"
                  : page === "Contact-directory"
                    ? "Contact Directory"
                    : page === "Inventory-management"
                      ? "Inventory Management"
                      : page === "Orders"
                        ? "Supply/Order Management"
                        : page}
              </Button>
            ))}
          </Box>

          {/* Search for desktop screen */}
          <Box
            sx={{
              // flexGrow: 1,
              display: {
                xs: "none",
                md: "flex",
                marginLeft: "22px",
                marginRight: "24px",
              },
            }}
          >
            {/* <SearchSelectDropdown
              freeSolo={true}
              size="small"
              label="Search"
              fullWidth={deviceType === "small-tablet" ? "200px" : "222px"}
              startIcon={
                <img
                  alt="search-icon"
                  src={searchIcon}
                  width={"16px"}
                  height={"16ppx"}
                />
              }
              options={searchFieldData}
              onSearchValueChangeCb={handleSearchChangeValue}
              onSelectSearchValue={handleSelectedSearchValue}
            /> */}
          </Box>

          {/* Switch to Provider dropdown - Desktop Only */}
          {showSwitchToProvider && (
            <Box
              sx={{
                display: {
                  xs: "none",
                  sm: "none",
                  md: "none",
                  lg: "flex",
                },
                marginRight: "16px",
              }}
            >
              <FacilityDropdown
                variant="desktop"
                onFacilitySelect={(facility) => {
                  console.log("Selected facility:", facility);
                  // Role switching is now handled automatically by FacilityDropdown
                }}
              />
            </Box>
          )}

          {/* Messaging icon */}
          <IconButton
            size="large"
            aria-label="show messaging"
            color="inherit"
            sx={{ marginRight: "24px" }}
            onClick={() => {
              navigate("/messaging");
            }}
          >
            <img src={messagingIcon} alt="messaging-icon" />
          </IconButton>

          {/* Notification icon */}
          {/* Notification icon */}
          {hasNew ? (
            <IconButton
              size="large"
              aria-label="show new notifications"
              color="inherit"
              sx={{ marginRight: "24px" }}
              onClick={() => {
                navigate("/notifications");
                if (hasNew) {
                  setHasNew(false); // Remove dot after opening
                }
              }}
            >
              <img src={notificationDot} alt="notification-icon" />
            </IconButton>
          ) : (
            <IconButton
              size="large"
              aria-label="show new notifications"
              color="inherit"
              sx={{ marginRight: "24px" }}
              onClick={() => {
                navigate("/notifications");
                if (hasNew) {
                  setHasNew(false); // Remove dot after opening
                }
              }}
            >
              <img src={notificationIcon} alt="notification-icon" />
            </IconButton>
          )}

          {/* User menu */}
          <Box sx={{ flexGrow: 0 }}>
            <div className="flex justify-center items-center gap-2">
              <Tooltip title="Open settings">
                <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                  <Avatar
                    style={{ background: "#795a5a" }}
                    alt={loggedInUserName || "UU"}
                    src="/static/images/avatar/2.jpg"
                  />
                </IconButton>
              </Tooltip>
              <img
                className="cursor-pointer"
                onClick={handleOpenUserMenu}
                src={bottomDirectionArrow}
                alt="bottom-arrow"
              />
            </div>

            <Menu
              sx={{ mt: "45px" }}
              id="menu-appbar"
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              {settings.map((setting) => (
                <MenuItem
                  key={setting}
                  onClick={() => handleCloseUserMenu(setting)}
                >
                  <Typography sx={{ textAlign: "center" }}>
                    {setting}
                  </Typography>
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Toolbar>
      </Container>
      {/* Drawer for mobile and tablet screens*/}
      {openMenu && (
        <EventEHRSmallScreenMenuDrawer
          open={openMenu}
          handleCloseDrawer={() => {
            setOpenMenu(false);
          }}
          handleMenuItem={(menuItem) => {
            switch (menuItem) {
              case "Dashboard":
                navigate("/dashboard");
                break;
              case "Reports":
                navigate("/reports");
                break;
              case "Facility-management":
                navigate("/facility-management");
                break;
              case "Inventory-management":
                navigate("/inventory-management");
                break;
              case "Orders":
                navigate("/orders");
                break;
              case "Documents":
                navigate("/documents");
                break;
              case "Contact-directory":
                navigate("/contact-directory");
                break;
              case "Tasks":
                navigate("/tasks");
                break;
              case "Settings":
                navigate("/settings");
                break;
            }
            setOpenMenu(false);
          }}
          searchFieldData={searchFieldData}
          handleSearchChangeValue={handleSearchChangeValue}
          handleSelectedSearchValue={handleSelectedSearchValue}
        />
      )}

      {/* BROADCAST MESSAGE POP-UP — the header renders on every screen, so a
          broadcast interrupts the user wherever they are. Only an OK button is
          passed, so it must be acknowledged (the dialog ignores backdrop clicks). */}
      <EventEHRConfirmationDialog
        alertTitle="Broadcast Message"
        alertDescription={
          broadcastNotification ? (
            <>
              {/* Source line (e.g. "Message from JamboCare Leadership Team") */}
              <span
                style={{
                  display: "block",
                  fontWeight: 600,
                  marginBottom: "6px",
                }}
              >
                {broadcastNotification?.title}
              </span>
              {/* The actual message ("<Sender>: <message>") */}
              <span style={{ display: "block", wordBreak: "break-word" }}>
                {broadcastNotification?.description}
              </span>
            </>
          ) : null
        }
        submitButtonTitle="OK"
        open={!!broadcastNotification}
        handleClose={() => dismissBroadcast()}
        // Fixed width so the modal doesn't resize with message length.
        paperSx={{ width: "420px", maxWidth: "90vw" }}
      />
    </AppBar>
  );
}

export default EventEMRResponsiveAppBar;
